import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { clipSearch, KIND_LABELS, searchParams, when, type CircleKind } from "../accounts";
import { errorText, unwrap } from "../problem";

const KINDS = Object.keys(KIND_LABELS) as CircleKind[];
const EVENT_LABELS = { upcoming: "Upcoming", live: "Live", ended: "Ended" } as const;

// Every page is audited (`circle.search`), so the list loads only when asked.
export function CirclesPage() {
  const [draft, setDraft] = useState({ q: "", kind: "" as CircleKind | "" });
  const [search, setSearch] = useState<{ q: string; kind: CircleKind | ""; run: number } | null>(null);
  const list = useInfiniteQuery({
    queryKey: ["circles", search],
    enabled: search !== null,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/circles", {
          params: {
            query: {
              ...searchParams(search!.q),
              ...(search!.kind ? { kind: search!.kind } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    // Nothing kept once the page closes, so the same search again is fetched (and audited) afresh.
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  function apply(event: FormEvent) {
    event.preventDefault();
    setSearch((previous) => ({ q: draft.q.trim(), kind: draft.kind, run: (previous?.run ?? 0) + 1 }));
  }

  const circles = list.data?.pages.flatMap((page) => page.circles) ?? [];
  const matched = list.data?.pages[0]?.matched;
  return (
    <>
      <h1>Circles</h1>
      <p className="muted">
        Circle metadata only: never places, locations or messages. Searches and opened circles are recorded in the audit
        log.
      </p>
      <form className="filters" onSubmit={apply}>
        <label>
          Kind
          <select
            value={draft.kind}
            onChange={(event) => setDraft({ ...draft, kind: event.target.value as CircleKind })}
          >
            <option value="">Any</option>
            {KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {KIND_LABELS[kind]}
              </option>
            ))}
          </select>
        </label>
        <label className="grow">
          Search
          <input
            type="search"
            value={draft.q}
            placeholder="Name, circle id or invite code (blank: newest circles)"
            onChange={(event) => setDraft({ ...draft, q: clipSearch(event.target.value) })}
          />
        </label>
        <button type="submit">Search</button>
      </form>
      {list.isFetching && !list.isFetchingNextPage && <p>Loading…</p>}
      {list.isError && <p className="error">{errorText(list.error)}</p>}
      {matched !== undefined && (
        <p className="muted">
          {matched.toLocaleString()} {matched === 1 ? "circle" : "circles"}
        </p>
      )}
      {circles.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>Created</th>
              <th className="num">Members</th>
            </tr>
          </thead>
          <tbody>
            {circles.map((circle) => (
              <tr key={circle.id}>
                <td>
                  <Link to={`/circles/${circle.id}`}>{circle.name}</Link>
                </td>
                <td>
                  {KIND_LABELS[circle.kind]}
                  {circle.event_status ? ` · ${EVENT_LABELS[circle.event_status]}` : ""}
                </td>
                <td>{when(circle.created_at)}</td>
                <td className="num">{circle.members}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list.hasNextPage && (
        <button
          type="button"
          className="button-quiet"
          onClick={() => void list.fetchNextPage()}
          disabled={list.isFetchingNextPage}
        >
          {list.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </>
  );
}
